<template>
  <div class="call-result-comment">
    <h3 class="title">
      {{ $t('callResult.comment.text') }}
      <BBtn
        type="default"
        size="mini"
        outline
        class="insert-summarized-result-button"
        :disabled="!summarizedResult"
        @click="clickInsertSummarizedResult"
      >
        <BIcon
          size="mini"
          outlined
        >
          convert_to_text
        </BIcon>
        {{ $t('transcription.insertSummarizationText') }}
      </BBtn>
    </h3>
    <div class="input-container">
      <BInput
        v-model="comment"
        type="textarea"
        resize="none"
        :rows="null"
      />
    </div>
    <div class="button-container">
      <BBtn
        type="primary"
        :loading="loading"
        :disabled="saveButtonDisabled"
        @click="clickSaveButton"
      >
        {{ $t('general.save.text') }}
      </BBtn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { CallResult } from '@/api/openapi';
import { CallResultApiService } from '@/api/user/resources/call_result';
import { useToast } from '@/composable/toast';
import { useWait } from '@/composable/vue-wait';

const props = defineProps<TProps>();
const emit = defineEmits<TEmits>();

type TProps = {
  callResult: CallResult | null;
  summarizedResult?: string;
};
type TEmits = {
  'update:comment': [string];
  'update:loading': [boolean];
}

const api = new CallResultApiService();
const { doActionWithWait, wait } = useWait();
const { successToast } = useToast();
const { t } = useI18n();

const saveButtonDisabled = ref(false);
const comment = ref('');
const loading = computed(() => wait.is(['updateCallResultCommentWait']));

watch(loading, (newValue) => {
  emit('update:loading', newValue);
});

watch(() => props.callResult?.comment, (newValue) => {
  comment.value = newValue || '';
});

const clickSaveButton = async () => {
  const callResultId = props.callResult?.id;
  if (!callResultId) return;
  await doActionWithWait('updateCallResultCommentWait', async () => {
    await api.updateComment({
      request: {
        callResultId,
        patchCallResultCommentRequest: {
          comment: comment.value,
        },
      },
    });
    emit('update:comment', comment.value);
  });
  successToast(t('general.save.doneOf', { item: t('callResult.comment.text') }));
};

const extractSummaryResult = (markdownText: string): string => {
  // 「## 要約結果」のセクションを探す（「概要」セクションを除く）
  // NOTE: プロンプトの内容に依存しているため、プロンプト変更時は対応が必要
  const summaryResultRegex = /(?:^|\n)##\s*要約結果\s*\n([\s\S]*?)(?=\n##|$)/;
  const match = markdownText.match(summaryResultRegex);

  if (match && match[1]) return match[1].trim();
  return '';
};
const clickInsertSummarizedResult = () => {
  if (!props.summarizedResult) return;
  
  const summaryResult = extractSummaryResult(props.summarizedResult);
  comment.value = comment.value ? `${comment.value}\n${summaryResult}` : summaryResult;
};
</script>

<style scoped lang="scss">
.call-result-comment {
  height: 100%;
  display: flex;
  flex-direction: column;

  .title {
    font-size: $fontsize-200;
    margin-bottom: $basespace-400;
    display: flex;
  }
  .input-container {
    flex-grow: 1;
    :deep(.b-input-wrapper) {
      height: 100%;
      .b-input-inner, .b-input, textarea {
        height: 100%;
      }
      textarea {
        min-height: 100% !important;
      }
    }
  }

  .button-container {
    @include m-flex-center;
    margin-top: $basespace-500;
  }
  .insert-summarized-result-button {
    margin-left: auto;
    padding-left: $basespace-200;
    padding-right: $basespace-200;
    .b-icon {
      margin-right: $basespace-50;
    }
  }
}
</style>
